import React, { useState, useRef, useEffect, useMemo } from "react";

// ═══════════════════════════════════════════════════════════════════
// AI Trading Advisor — Auto-summary + simple chat
//
// Integration in trader_form.jsx:
//   1. Import: import AITradingAdvisor from '../../components/AITradingAdvisor';
//   2. Add tab: <Tab label="AI Advisor" value="advisor" />
//   3. Render: {activeTab === 'advisor' && <AITradingAdvisor bots={bots} botHistories={liveBotHistories} futuresInfo={futuresInfo} />}
// ═══════════════════════════════════════════════════════════════════

const SYSTEM_PROMPT = `You are the AI Trading Advisor for the CryptoLogics platform. You analyze the user's crypto trading bots and give clear, concise insights.

Rules:
- Be brief and direct. Use exact numbers from their data.
- Use **bold** for key metrics.
- For the initial summary: cover overall PnL, best/worst bot, any active errors or warnings, and one suggestion. Keep under 200 words.
- For follow-ups, answer specifically using their data. Stay concise.
- Note this is algorithmic analysis, not financial advice.`;

function buildContext(bots, botHistories, futuresInfo) {
  return (bots || []).map((bot) => {
    let ti = {};
    try { ti = typeof bot.trade_info === "string" ? JSON.parse(bot.trade_info) : bot.trade_info || {}; } catch { ti = {}; }
    const history = (botHistories?.[bot.id] || []).slice(-15).map((e) => {
      const text = typeof e.text === "string" ? (() => { try { return JSON.parse(e.text); } catch { return e.text; } })() : e.text;
      return { timestamp: e.timestamp, ...text };
    });
    return {
      id: bot.id, name: bot.name, symbol: bot.symbol, interval: bot.interval,
      status: bot.status, marketType: ti.marketType || "spot",
      leverage: ti.futures?.leverage || 1,
      logic: { buy: bot.logic?.buy, sell: bot.logic?.sell, shortOpen: bot.logic?.shortOpen, shortClose: bot.logic?.shortClose },
      risk: { profitStop: bot.logic?.profitStop, lossStop: bot.logic?.lossStop, trailingStop: bot.logic?.trailingStop },
      stats: bot.stats || null,
      errorMessage: bot.errorMessage || null,
      guardState: bot.guardTag ? { tag: bot.guardTag, reason: bot.guardReason } : null,
      lastTrade: bot.lastTradeSide ? { side: bot.lastTradeSide, price: bot.lastTradePrice, time: bot.lastTradeTs } : null,
      futuresData: futuresInfo?.[bot.id] || null,
      recentTrades: history,
    };
  });
}
const API_BASE = process.env.REACT_APP_API_BASEURL || "http://localhost:3005";

async function streamClaude(messages, onChunk) {
  const res = await fetch(`${API_BASE}/api/ai-advisor`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ messages, system: SYSTEM_PROMPT }),
  });
  if (!res.ok) {
    let errMsg = `API error ${res.status}`;
    try { const e = await res.json(); errMsg = e.error || errMsg; } catch {}
    throw new Error(errMsg);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let full = "", buf = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const chunks = buf.split("\n\n");
    buf = chunks.pop() || "";
    for (const chunk of chunks) {
      if (!chunk.startsWith("data: ")) continue;
      const d = chunk.slice(6).trim();
      if (d === "[DONE]") break;
      try {
        const p = JSON.parse(d);
        if (p.error) throw new Error(p.error);
        if (p.text) { full += p.text; onChunk(full); }
      } catch (e) { if (e.message && e.message !== d) throw e; }
    }
  }
  return full;
}

function md(text) {
  if (!text) return "";
  return text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, '<code style="background:rgba(255,255,255,0.06);padding:1px 4px;border-radius:3px;font-size:0.85em">$1</code>')
    .replace(/^[\-\*] (.+)$/gm, '<div style="padding:1px 0 1px 12px">• $1</div>')
    .replace(/^\d+\. (.+)$/gm, (m, c) => `<div style="padding:1px 0 1px 12px">${m.match(/^\d+/)[0]}. ${c}</div>`)
    .replace(/\n\n/g, '<div style="height:10px"></div>')
    .replace(/\n/g, "<br/>");
}

export default function AITradingAdvisor({ bots = [], botHistories = {}, futuresInfo = {} }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [autoSent, setAutoSent] = useState(false);
  const endRef = useRef(null);

const [fetchedHistories, setFetchedHistories] = useState({});

  // Fetch trade histories directly so we don't depend on websocket load timing
  useEffect(() => {
    const userId = parseInt(localStorage.getItem('userId'), 10);
    if (!userId) return;

    const API_BASE = process.env.REACT_APP_API_BASEURL || "http://localhost:3005";
    const token = localStorage.getItem('token');

    fetch(`${API_BASE}/api/userhistory/all_by_user/${userId}`, {
      headers: token ? { Authorization: token } : {},
    })
      .then(res => res.json())
      .then(data => {
        if (!Array.isArray(data)) return;
        const grouped = {};
        data.forEach(entry => {
          const botId = entry.bot_id;
          let text = entry.buy_sell_history;
          try { text = JSON.parse(text); } catch {}
          if (!grouped[botId]) grouped[botId] = [];
          grouped[botId].push({ timestamp: new Date(entry.date).toLocaleString(), text });
        });
        setFetchedHistories(grouped);
      })
      .catch(err => console.warn('AI advisor: failed to fetch histories', err));
  }, []);

  // Merge: prefer fetched histories, fall back to prop
  const mergedHistories = useMemo(() => {
    const merged = { ...fetchedHistories };
    for (const [botId, entries] of Object.entries(botHistories || {})) {
      if (!merged[botId] || (entries.length > (merged[botId]?.length || 0))) {
        merged[botId] = entries;
      }
    }
    return merged;
  }, [botHistories, fetchedHistories]);

  const context = useMemo(
    () => buildContext(bots, mergedHistories, futuresInfo),
    [bots, mergedHistories, futuresInfo]
  );  
  
  
  const botIdsKey = useMemo(() => (bots || []).map(b => b.id).sort().join(","), [bots]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Auto-summary when bots load
  useEffect(() => {
    if (autoSent || bots.length === 0 || streaming) return;
    setAutoSent(true);
    send("Give me a brief summary of my portfolio and trading bots.", true);
  }, [botIdsKey]);

  const send = async (text, isAuto = false) => {
    if (!text.trim() || streaming) return;
    setError(null);

    const ctxBlock = `<trading_data>${JSON.stringify(context)}</trading_data>`;
    const apiMsgs = [
      ...messages.map(m => ({ role: m.role, content: m.role === "user" ? m.raw : m.content })),
      { role: "user", content: `${ctxBlock}\n\n${text}` },
    ];

    if (!isAuto) {
      setMessages(prev => [...prev, { role: "user", content: text, raw: `${ctxBlock}\n\n${text}` }]);
      setInput("");
    }
    setMessages(prev => [...prev, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      await streamClaude(apiMsgs, (partial) => {
        setMessages(prev => { const c = [...prev]; c[c.length - 1] = { role: "assistant", content: partial }; return c; });
      });
    } catch (err) {
      setError(err.message);
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div style={{
      display: "flex", flexDirection: "column", height: "calc(100vh - 240px)",
      minHeight: 400, background: "#0e0e0e", borderRadius: 12,
      border: "1px solid #222", overflow: "hidden",
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    }}>
      {/* Messages */}
      
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 20px 8px" }}>
        {bots.length === 0 && messages.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#555", fontSize: 14 }}>
            Create a trading bot first to get AI analysis.
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} style={{ marginBottom: 14 }}>
            {msg.role === "user" && (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div style={{
                  background: "#0f1218", border: "1px solid #252545",
                  borderRadius: "12px 12px 4px 12px", padding: "10px 14px",
                  maxWidth: "75%", color: "#c8c8e0", fontSize: 13.5, lineHeight: 1.5,
                }}>
                  {msg.content}
                </div>
              </div>
            )}
            {msg.role === "assistant" && (
              <div style={{
                background: "#141414", border: "1px solid #1e1e1e",
                borderRadius: "12px 12px 12px 4px", padding: "12px 16px",
                color: "#ccc", fontSize: 13.5, lineHeight: 1.6,
              }}>
                {msg.content
                  ? <div dangerouslySetInnerHTML={{ __html: md(msg.content) }} />
                  : <span style={{ color: "#555" }}>Analyzing...</span>}
              </div>
            )}
          </div>
        ))}

        {error && (
          <div style={{
            background: "#1a0a0a", border: "1px solid #331a1a", borderRadius: 8,
            padding: "8px 12px", color: "#d97b72", fontSize: 12, marginBottom: 8,
          }}>
            {error}
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div style={{ padding: "8px 16px 14px", borderTop: "1px solid #0f1218" }}>
        <form onSubmit={(e) => { e.preventDefault(); send(input); }} style={{
          display: "flex", gap: 8, background: "#141414", border: "1px solid #222",
          borderRadius: 10, padding: "3px 3px 3px 14px",
        }}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about your trading..."
            disabled={streaming || bots.length === 0}
            style={{
              flex: 1, background: "none", border: "none", outline: "none",
              color: "#d0d0d0", fontSize: 13.5, fontFamily: "inherit", padding: "8px 0",
            }}
          />
          <button type="submit" disabled={!input.trim() || streaming} style={{
            background: input.trim() && !streaming ? "#d9a441" : "#222",
            border: "none", borderRadius: 8, padding: "7px 14px",
            color: input.trim() && !streaming ? "#000" : "#555",
            fontWeight: 600, fontSize: 13, cursor: input.trim() && !streaming ? "pointer" : "default",
          }}>
            {streaming ? "..." : "Ask"}
          </button>
        </form>
        <div style={{ textAlign: "center", color: "rgba(255,255,255,0.06)", fontSize: 10, marginTop: 6 }}>
          Not financial advice
        </div>
      </div>
    </div>
  );
}